<template>
  <loading v-if="response === null" :active="true" :is-full-page="false" color="blue" loader="spinner"></loading>

  <b-container v-else class="mt-2">
    <vue-headful :title="setPageTitle('productDetails', response.name)"></vue-headful>
    <loading :active="loading" :is-full-page="true" color="blue" loader="spinner"></loading>

    <b-row>
      <b-col cols="12" class="detailsTemplateProductName">
        <span class="font-weight-bold mr-2">{{ response.name }}</span>

        <template v-if="!response.isProductInTest">
          <followed-icon :is-followed="response.isFollowed" @click="() => changeFollow(response)"></followed-icon>
        </template>
        <span v-else>
          <pit-svg></pit-svg>
        </span>
      </b-col>
    </b-row>
    <b-row class="pb-3" no-gutters>
      <b-col cols="12" class="detailsTemplateIssuer">{{ response.issuerName }}</b-col>
    </b-row>

    <b-row class="my-3" no-gutters>
      <b-col cols="1" align-self="center">
        {{ $tc('productDetails.revisions', get_countRevision, { count: get_countRevision }) }}
      </b-col>
      <b-col cols="2">
        <custom-multi-select
          v-if="!has_justOneRevision"
          :options="revisions"
          size="sm"
          maintain-original-order
          v-model="transformSelectedRevision"
          data-test="revision-date"
          :default-text="$d(new Date(selectedRevision), 'short')"
          :placeholder="$t('common.search')"
        >
        </custom-multi-select>
        <b-input size="sm" v-else :value="$d(new Date(transformSelectedRevision[0]), 'short')" readonly />
      </b-col>
    </b-row>

    <b-row>
      <!-- Sidebar -->
      <b-col cols="4" class="characteristics">
        <detail-section
          v-for="section in response.sidebar"
          :key="section.sectionKey"
          :section="section"
        ></detail-section>
      </b-col>

      <!-- Tabs -->
      <b-col cols="8">
        <b-tabs v-model="activeTabIndex" nav-class="mainNav" class="fairmat-primary-tabs main-card px-3" data-test="container-product-new">
          <!-- "Prodotto" — wraps every tab_key except InvestmentOptions in its own secondary tab row,
          mirroring views/ProductDetails.vue's outer/inner b-tabs nesting (:67-267) that the original
          flat-Tabs[] design deliberately dropped (DETAIL_PAGE_DESIGN_FE.md) — restored per explicit
          user request. -->
          <b-tab lazy>
            <template v-slot:title>{{ $t('productDetails.mopLabels.product') }}</template>
            <b-tabs
              v-model="activeSecondaryTabIndex"
              class="fairmat-secondary-tabs mt-4"
              data-test="container-product-new-secondary"
            >
              <b-tab v-for="tab in productTabs" :key="tab.tabKey" :title="tabLabel(tab.tabKey)" lazy>
                <!-- Product-level cross-links, KpiKid tab only (product-level context only). -->
                <b-container v-if="tab.tabKey === 'KpiKid'" class="text-right mt-3">
                  <b-row v-if="canViewIndicatorsHistory">
                    <b-col cols="5" offset="7" class="pointer fairmatPrimary" @click="pushToRoute('indicatorsHistory')">
                      <span v-t="'productDetails.toIndicatorsHistory'"></span>
                      <i class="ml-2 fas fa-chevron-right"></i>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="5" offset="7" class="pointer fairmatPrimary" @click="pushToRoute('marketComparison')">
                      <span v-t="'productDetails.toMarketComparer'" data-test="marketCompPerProduct"></span>
                      <i class="ml-2 fas fa-chevron-right"></i>
                    </b-col>
                  </b-row>
                </b-container>
                <detail-section
                  v-for="section in tab.sections"
                  :key="section.sectionKey"
                  :section="section"
                ></detail-section>
              </b-tab>
              <!-- "Benchmarking" — NOT catalog-driven (no tab_key from the backend), gated entirely
              client-side by permission + per-product ownership (see canViewBenchmarking). Last
              secondary tab.
              Explicit mt-3 on both branches' first child — the generic
              `.fairmat-secondary-tabs /deep/ .tab-pane > *:first-child { margin-top: 1rem }` rule
              below is meant to cover exactly this gap, but this tab's content is two levels deep in
              conditional <template>s, and SimpleBox/NoContent's own root apparently still ends up
              touching the chip row without an explicit class — same "genuinely missing gap" fix as
              TargetMarketSection.vue's own root mt-3. -->
              <b-tab v-if="canViewProductBenchmarking" :title="$t('productDetails.benchmarking')" lazy>
                <template v-if="response.benchmarkingAccess.disabledReason != null">
                  <b-row class="mt-3">
                    <b-col>
                      <no-content
                        :icon-name="'exclamation-circle'"
                        :icon-class="['mb-3', 'fa-3x']"
                        :text="benchmarkingDisabledReasonMessage"
                      ></no-content>
                    </b-col>
                  </b-row>
                </template>
                <template v-else>
                  <benchmarking
                    class="mt-3"
                    :fairmat-product-score-update-key="fairmatProductScoreKey"
                    :scoring-profiles-list="getScoringProfilesList"
                    :current-profile-id="getCurrentProfileId"
                    :associated-scoring-profile="getScoringProfileAssociated"
                    :product-score="getProductScore"
                    :loading-product-score="getLoadingProductScore"
                    :success-on-associate="successOnAssociate"
                    :is-product-in-test="response.isProductInTest"
                    @on-click-create="createNewScoringProfile"
                    @on-change-scoring-profile="onChangeScoringProfile"
                  ></benchmarking>
                </template>
              </b-tab>
              <b-tab v-if="canViewProductEfficiencyTests" :title="$t('productDetails.efficiencyTests')" lazy>
                <product-efficiency-tests
                  v-if="productIsin"
                  class="mt-3"
                  :isin="productIsin"
                  :product="efficiencyTestProduct"
                ></product-efficiency-tests>
              </b-tab>
            </b-tabs>
          </b-tab>
          <!-- "Opzioni di investimento" — only present when the backend actually sent an
          InvestmentOptions tab (StructureType==Mop; a SOP/IC response never has one, same "absent
          tab_key ⇒ no tab" contract every other tab already follows). -->
          <b-tab v-if="investmentOptionsTab" lazy>
            <template v-slot:title>
              {{ $t('productDetails.mopLabels.options') }}
              <span class="ml-2">
                <b-badge>{{ investmentOptionsCount }}</b-badge>
              </span>
            </template>
            <detail-section
              v-for="section in investmentOptionsTab.sections"
              :key="section.sectionKey"
              :section="section"
            ></detail-section>
          </b-tab>
          <b-tab v-if="showDocumentationTab" :title="$t('productDetails.documentationTab')" lazy>
            <documentations :value="documentation"></documentations>
          </b-tab>
          <!-- Reusable FKE narrative, here the product description. Validator-only tab (canViewFkeSummary).
               Not lazy: it loads on page entry so it is ready when the tab is opened. -->
          <b-tab v-if="canViewFkeSummary" :title="$t('productDetails.productDescription')">
            <div class="product-description-tab">
              <fke-narrative-summary
                :title="$t('productDetails.productDescription')"
                :query="productSummaryQuery"
                :revision-ids="response.revisionIds || []"
                :cache-key="productSummaryCacheKey"
                :active="true"
              ></fke-narrative-summary>
            </div>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// Config-driven Product Detail page (DETAIL_PAGE_DESIGN_FE.md). loadData() calls the
// POST /api/detail-page/product endpoint; Sidebar/Tabs render generically via DetailSection.vue,
// which has a typed component or field-catalog coverage for every SectionKey reachable from an
// IC response.
import { createNamespacedHelpers } from 'vuex'
import { createHelpers } from 'vuex-map-fields'

import Documentations from '../components/ProductDetails/Documentations'
import CustomMultiSelect from '../components/Lib/CustomMultiSelect'
import DetailSection from '../components/ProductDetails/DetailSection'
import Benchmarking from '../components/ProductDetails/Benchmarking'
import ProductEfficiencyTests from '../components/ProductDetails/ProductEfficiencyTests'
import NoContent from '../components/Lib/NoContent'
import FollowedIcon from '../components/Lib/FollowedIcon'
import PitSvg from '../components/Lib/PitSvg'
import FkeNarrativeSummary from '../components/Lib/FkeNarrativeSummary'
import { base64UrlDecode, base64UrlEncode, isBase64Url } from '../utils/sanitizer'
import { isValidDate } from '../utils/date'
import { detailTabLabel } from '../utils/detailFieldFormat'
import { FKE_SUMMARY_PERMISSION } from '../utils/fkeSummary'

// FKE query for the product-description narrative (different from the efficiency-test peer query;
// FkeNarrativeSummary is the shared block).
const PRODUCT_SUMMARY_QUERY =
  'Produci una descrizione professionale, imparziale e orientata al cliente del prodotto, utilizzando ' +
  'esclusivamente le informazioni presenti nei documenti disponibili (KID, DIP, CGA, SFDR). Descrivi ' +
  'obiettivi, sottostanti o attivi investiti, durata, modalità di funzionamento e struttura dei ' +
  'rendimenti/payoff. Riporta costi complessivi (RIY o metriche equivalenti), livello di rischio (SRI e ' +
  'altri rischi rilevanti), rischio emittente e liquidità. Illustra gli scenari di rimborso o rendimento ' +
  'previsti dal prodotto. Evidenzia eventuali caratteristiche ESG/SFDR, garanzie, opzioni o vincoli ' +
  'contrattuali. Utilizza uno stile narrativo e non promozionale.'

const { mapActions, mapGetters } = createNamespacedHelpers('productDetails')
const { mapFields } = createHelpers({
  getterType: 'productDetails/getField',
  mutationType: 'productDetails/updateField',
})

export default {
  name: 'product-details-new',
  components: {
    Documentations,
    CustomMultiSelect,
    DetailSection,
    Benchmarking,
    ProductEfficiencyTests,
    NoContent,
    FollowedIcon,
    PitSvg,
    FkeNarrativeSummary,
  },
  // Component adapters (e.g. typedSections/CostsProfileSection.vue) sometimes need a
  // datum that belongs to a DIFFERENT section than the one they were dispatched for (e.g. `currency`,
  // a Characteristics/Sidebar field, needed to format CostsProfile's monetary values) —
  // DetailSection.vue's dispatch deliberately only ever hands a component its OWN section's
  // payload/fields, so this is threaded via provide/inject instead of a prop chain, keeping that
  // dispatch point asset-class/section-agnostic. `this` (not a plain object) is provided so `currency`
  // below stays reactive for injectors — see https://v2.vuejs.org/v2/api/#provide-inject.
  provide() {
    return { detailPage: this }
  },
  props: {
    productName: {},
    issuer: {},
    referenceDate: {},
    investmentOptionName: { default: null },
  },
  data() {
    return {
      selectedRevision: null,
      loading: false,
      response: null,
      documentation: [],
      // In-page drilldown: the currently selected investment option, if any.
      // selectedInvestmentOption keeps the clicked row's own {id, name} (the drilldown response
      // itself carries no name, InvestmentOptionDetailPageResponse.cs) so InvestmentOptionDetailPanel.vue
      // can show a header immediately, before/independent of the fetch resolving.
      selectedInvestmentOption: null,
      investmentOptionResponse: null,
      loadingInvestmentOption: false,
      // Deep-link support: index into the
      // OUTER b-tabs below — "Prodotto" is always 0, "Opzioni di investimento" is always 1 when
      // rendered (bootstrap-vue only assigns indices to tabs that actually render, so this stays
      // correct regardless of whether "Documentazione" follows it). Set to 1 in loadData() when the
      // URL already carries an investmentOptionName, so the tab opens pre-activated instead of
      // requiring a manual click.
      activeTabIndex: 0,
      // Static, never mutated. Only exists
      // to satisfy fairmat-product-score's :key prop; nothing in this page ever needs to force a
      // re-mount of the widget.
      fairmatProductScoreKey: 0,
      // Keyed by
      // BenchmarkingDisableReasons' (OpenKid.Application.DetailPage) implicit int values, which have
      // no JsonStringEnumConverter registered anywhere in this app (same raw-number convention as
      // SidType, see InvestmentOptionDetailPanel.vue's own comment).
      benchmarkingDisabledReasonMessageMap: {
        0: 'expiredProduct',
        1: 'notCurrentRevision',
      },
    }
  },
  computed: {
    // Revisions handling (revisions/has_justOneRevision/transformSelectedRevision).
    // response.otherRevisions comes from the backend (DetailPageResponse.OtherRevisions).
    get_countRevision() {
      return this.revisions ? this.revisions.length : 0
    },
    // The backend catalog's TargetMarket row seeds `required_permission = NULL` (unlike e.g.
    // AdditionalInfo's AdditionalInfoAccess gate) — it is NOT enforced server-side, so the tab is
    // excluded from productTabs() below the same way views/ProductDetails.vue:147 gates it client-side
    // (`v-if="canViewTargetMarket"`), to avoid a real access-control regression rather than a
    // cosmetic one. See typedSections/TargetMarketSection.vue's own comment.
    permissions() {
      return this.$store.getters.getPermissions.userPermissions || {}
    },
    canViewTargetMarket() {
      return this.permissions.canViewTargetMarket
    },
    // Same FE-only-permission situation as canViewTargetMarket above, consumed by
    // typedSections/InvestmentOptionsProfileSection.vue via the detailPage inject — see that
    // component's own comment for why it's gated there instead of at this tab-filtering layer.
    canViewTypeCategory() {
      return this.permissions.canViewTypeCategory
    },
    // Same permission views/ProductDetails.vue's own canViewBenchmarking read
    // (GetUserInfoEndpoint.cs → UserPermissionsDto.CanViewScoringProfiles) — a per-user fact,
    // independent of the per-product BenchmarkingAccess the backend sends on the response. Both
    // must hold for the tab to show at all.
    canViewScoringProfiles() {
      return this.permissions.canViewScoringProfiles
    },
    canViewProductBenchmarking() {
      return this.canViewScoringProfiles && this.response.benchmarkingAccess.isOwnedByUser
    },
    // Efficiency Tests exist in some asset classes only (Investment Certificates): the permission is
    // answered for the asset class of the page, so the tab never leads to a page the asset class lacks.
    canViewProductEfficiencyTests() {
      return this.canViewProductBenchmarking && this.$store.getters.can('canViewEfficiencyTest')
    },
    // Product-description FKE narrative tab: validator-only (shared permission with the efficiency-test
    // peer summary). `can` resolves asset-class-scoped first, then global userPermissions.
    canViewFkeSummary() {
      return this.$store.getters.can(FKE_SUMMARY_PERMISSION)
    },
    productSummaryQuery() {
      return PRODUCT_SUMMARY_QUERY
    },
    // Cache key per document revision set (language is appended inside FkeNarrativeSummary), so each
    // product revision caches separately.
    productSummaryCacheKey() {
      const ids = this.response.revisionIds || []
      return ids.length ? 'product.' + ids.join('-') : ''
    },
    // An IBIPs product in test has no documentation of its own worth showing. Last tab of the outer
    // row, so hiding it never shifts activeTabIndex's Prodotto/Opzioni di investimento indices.
    // Compared against IC rather than 'ibips': the slug is null for single-asset-class accounts.
    showDocumentationTab() {
      const isInvestmentCertificates = this.$store.getters.getSelectedAssetClass === 'investment-certificates'
      return !this.response.isProductInTest || isInvestmentCertificates
    },
    benchmarkingDisabledReasonMessage() {
      const key = this.benchmarkingDisabledReasonMessageMap[this.response.benchmarkingAccess.disabledReason]
      return this.$t('benchmarking.disabledReasonType.' + key)
    },
    ...mapGetters([
      'getScoringProfilesList',
      'getCurrentProfileId',
      'getScoringProfileAssociated',
      'getProductScore',
      'getProductScoreInvestment',
      'getLoadingProductScore',
    ]),
    ...mapFields(['successOnAssociate']),
    // Two-row tab header (user-confirmed, restores views/ProductDetails.vue's own outer/inner
    // nesting): row 1 is Prodotto/Opzioni di investimento/Documentazione; row 2 (inside Prodotto) is
    // whatever tab_keys the backend actually sent, in its own order — response.tabs stays the flat
    // array DETAIL_PAGE_DESIGN.md's catalog always produces, InvestmentOptions is just singled out of
    // it for its own top-level tab instead of being grouped with the rest, TargetMarket filtered per
    // the permission note above.
    productTabs() {
      return this.response.tabs.filter(
        (tab) => tab.tabKey !== 'InvestmentOptions' && (tab.tabKey !== 'TargetMarket' || this.canViewTargetMarket)
      )
    },
    // Ordered keys of the secondary tab row actually rendered inside "Prodotto" — mirrors
    // productTabs()'s own v-for order, plus Benchmarking (not catalog-driven, appended last, see its
    // own <b-tab> comment). Backs activeSecondaryTabIndex's URL round-trip below: the query stores a
    // KEY, not a raw bootstrap-vue index, so it survives the tab set changing shape between products/
    // permissions/revisions (an index would silently point at the wrong chip).
    secondaryTabKeys() {
      return [
        ...this.productTabs.map((tab) => tab.tabKey),
        ...(this.canViewProductBenchmarking ? ['Benchmarking'] : []),
        ...(this.canViewProductEfficiencyTests ? ['EfficiencyTests'] : []),
      ]
    },
    // Deep-linkable / back-button- and reload-safe active secondary chip, via the `tab` query param
    // (shared with InvestmentOptionDetailPanel.vue's own secondary tab row — the two are never
    // visible at once, so one query key unambiguously covers both). Falls back to the
    // 'currentSecondaryTabKey' localStorage entry (same cross-page "remembered last choice" pattern
    // already used for scoring profiles, see productDetails.js's loadScoringProfiles/
    // currentScoringProfileId) — user-confirmed: opening a DIFFERENT product's detail page with no
    // tab query of its own should still land on the chip you were last on, not always reset to the
    // first one. "quando possibile": an unknown/no-longer-available key at either step (stale link,
    // Benchmarking not owned, this product has no Payout tab, ...) falls through instead of erroring.
    activeSecondaryTabIndex: {
      get() {
        const fromUrl = this.secondaryTabKeys.indexOf(this.$route.query.tab)
        if (fromUrl !== -1) {
          return fromUrl
        }
        const fromStorage = this.secondaryTabKeys.indexOf(localStorage.getItem('currentSecondaryTabKey'))
        return fromStorage === -1 ? 0 : fromStorage
      },
      set(newIndex) {
        const key = this.secondaryTabKeys[newIndex]
        if (key == null) {
          return
        }
        localStorage.setItem('currentSecondaryTabKey', key)
        this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {})
      },
    },
    investmentOptionsTab() {
      return this.response.tabs.find((tab) => tab.tabKey === 'InvestmentOptions')
    },
    // Badge next to the "Opzioni di investimento" tab title — mirrors views/ProductDetails.vue's own
    // get_totalSidKiid (get_productSidCount + get_productKiid.length), just read off
    // InvestmentOptionsListPayload (sids/kiids) instead of the old flat ProductDetailDto.
    investmentOptionsCount() {
      if (this.investmentOptionsTab == null) return 0
      const list = this.investmentOptionsTab.sections.find((s) => s.sectionKey === 'InvestmentOptionsList')
      if (list == null || list.payload == null) return 0
      return (list.payload.sids || []).length + (list.payload.kiids || []).length
    },
    // Sidebar/Characteristics field — present for every asset class (IBIPs got it in migration .81,
    // added specifically because a typed section needed it; see DETAIL_PAGE_DESIGN.md). Null only
    // while response hasn't loaded yet.
    currency() {
      return this.characteristicField('currency')
    },
    // Scopes the "Test di efficienza" secondary tab (ProductEfficiencyTests.vue) to this product.
    productIsin() {
      return this.characteristicField('isin')
    },
    // Product in test handed to EfficiencyTestsCreate.vue — same fields EfficiencyTestsList.vue's
    // onProductPicked builds from its own /api/detail-page/product call, which is this page's response.
    efficiencyTestProduct() {
      const characteristics = this.response.sidebar.find((s) => s.sectionKey === 'Characteristics')
      return {
        productId: this.response.productId,
        name: this.response.name,
        issuerCode: this.response.issuerCode,
        referenceDate: this.response.referenceDate,
        characteristics: (characteristics && characteristics.fields) || [],
      }
    },
    revisions() {
      if (this.response == null || this.response.referenceDate == null) return []
      const otherRevisions = this.response.otherRevisions || []
      const dates = otherRevisions.map((item) => this.formatReferenceDate(item.referenceDate))
      const currentDate = this.formatReferenceDate(this.response.referenceDate)
      return [...dates, currentDate]
        .sort()
        .reverse()
        .map((date) => ({
          value: date,
          text: this.$d(new Date(date), 'short'),
          obj: otherRevisions.find((obj) => this.formatReferenceDate(obj.referenceDate) === date),
        }))
    },
    has_justOneRevision() {
      return this.revisions.length === 1
    },
    // The history of the indicators is a line per revision: a product in test has none, and with a
    // single revision there is nothing to follow over time.
    canViewIndicatorsHistory() {
      return !this.response.isProductInTest && !this.has_justOneRevision
    },
    transformSelectedRevision: {
      get() {
        return [this.selectedRevision]
      },
      set(newVal) {
        if (Array.isArray(newVal) && newVal.length > 0 && this.selectedRevision !== newVal[0]) {
          this.selectedRevision = newVal[0]
        }
      },
    },
  },
  watch: {
    selectedRevision(newValue, oldValue) {
      if (oldValue == null) {
        return
      }
      this.loadData().then(() => {
        this.$router
          .replace({
            name: 'productDetails',
            params: {
              productName: this.productName,
              issuer: this.issuer,
              referenceDate: newValue,
              investmentOptionName: this.investmentOptionName,
            },
            // Carries the `tab` query param across a revision switch too, for the same reason as
            // selectInvestmentOption() above — activeSecondaryTabIndex's own fallback-to-first-tab
            // already handles the case where the new revision's tab set doesn't have that key.
            query: this.$route.query,
          })
          .catch(() => {})
      })
    },
  },
  methods: {
    ...mapActions(['loadProductDocumentation', 'loadScoringProfiles', 'loadProductScore', 'changeScoringProfile', 'loadScoringProfileFilters']),

    // Shared by BOTH Benchmarking instances (product-level, here, and per-investment-option in
    // InvestmentOptionDetailPanel.vue via the detailPage inject). One shared
    // currentProfileId/productScore selection across both panels.
    //
    // When a profile is pre-selected via the currentScoringProfileId localStorage key (the
    // "Compara col mercato" cross-page hand-off), the active secondary tab is not auto-switched to
    // Benchmarking: this page's secondary <b-tabs> has no v-model to hook that into. The score
    // itself still loads automatically (loadProductScore below).
    onChangeScoringProfile(data) {
      this.successOnAssociate = false

      this.changeScoringProfile(data)
      this.loadProductScore({
        scoringProfileId: data.id,
        investmentOptionName: this.investmentOptionName != null ? base64UrlDecode(this.investmentOptionName) : null,
      })
    },

    createNewScoringProfile() {
      this.loading = true
      this.loadScoringProfileFilters(this.response.productId)
        .then((data) => {
          this.$router.push({
            name: 'scoringProfilesCreate',
            params: { filters: data.filterCollection },
          })
        })
        .catch((e) => {
          this.httpErrorsHandler(e)
        })
        .finally(() => {
          this.loading = false
        })
    },

    tabLabel(tabKey) {
      return detailTabLabel(this, tabKey)
    },

    // Generic Sidebar/Characteristics field lookup — the same "look at a DIFFERENT section's own
    // payload/fields" need `currency` above already had (§ typedSections/CostsProfileSection.vue's
    // own comment on why this goes through provide/inject rather than a prop chain), generalized so
    // a new typed section doesn't need its own bespoke computed here for every Characteristics field
    // it happens to need (e.g. AdditionalInfoSection.vue's productType/premiumType, IBIPs pass).
    characteristicField(key) {
      if (this.response == null) return null
      const characteristics = this.response.sidebar.find((s) => s.sectionKey === 'Characteristics')
      const field = characteristics && characteristics.fields ? characteristics.fields.find((f) => f.key === key) : null
      return field ? field.value : null
    },

    // In-page drilldown: called from InvestmentOptionsListSection.vue (SID/KIID
    // row click) and InvestmentOptionsProfileSection.vue (its chart-bar-click modal, product-level
    // KpiKid tab) via the detailPage inject — same "reach the page's own state through the shared
    // inject object" pattern as characteristicField/canViewTargetMarket/canViewTypeCategory above,
    // generalized one step further since this one also needs to trigger a fetch, not just read
    // already-loaded data. GET /api/detail-page/investmentOption/{id} — the sibling endpoint
    // built specifically for this, reusing every typed section fed a different
    // payload (InvestmentOptionProductScope's synthetic-Product mechanism, backend-side).
    //
    // activeTabIndex = 1: a caller outside the "Opzioni di investimento" tab (the KpiKid modal)
    // needs this page to switch there to show the drill-down at all; a caller already inside it
    // (InvestmentOptionsListSection.vue) just gets a harmless no-op re-assignment.
    //
    // URL update: $router.replace (not push, avoids spamming browser history on every row click)
    // updating just investmentOptionName, same route/params shape the selectedRevision watcher above
    // already uses.
    selectInvestmentOption(id, name) {
      this.activeTabIndex = 1
      this.selectedInvestmentOption = { id, name }
      this.investmentOptionResponse = null
      this.loadingInvestmentOption = true
      this.$router
        .replace({
          name: 'productDetails',
          params: {
            productName: this.productName,
            issuer: this.issuer,
            referenceDate: this.selectedRevision,
            investmentOptionName: base64UrlEncode(name),
          },
          // Carries the `tab` query param (if any) across the option switch — user-confirmed: the
          // secondary chip selected in InvestmentOptionDetailPanel.vue should follow you from one
          // option to the next, not reset. Naming the params object above already drops any query
          // that isn't restated explicitly here.
          query: this.$route.query,
        })
        .catch(() => {})
      this.get('/api/detail-page/investmentOption/' + id)
        .then((resp) => {
          if (resp != null) {
            this.investmentOptionResponse = resp
          }
        })
        .catch((e) => this.httpErrorsHandler(e))
        .finally(() => {
          this.loadingInvestmentOption = false
        })
    },

    // TargetMarketSection.vue's own @option-clicked (a click inside the shared FairmatTargetMarket
    // widget — any of its knowledge/experience, risk-tolerance, return-profile, or time-horizon
    // breakdowns, all bubbling through the same single event) mirrors views/ProductDetails.vue's own
    // `@option-clicked="(opt) => openInvestmentOptionDetails(opt.name)"` — the widget only ever gives
    // a NAME, not an id, unlike every other entry point into selectInvestmentOption() so far. Looked
    // up here (not inside TargetMarketSection.vue itself) against the same InvestmentOptionsList
    // payload investmentOptionsCount already reads, since that's the one place in this component tree
    // holding both sids and kiids together. For IC (always SOP), TargetMarketIndicatorsDto's
    // per-option breakdowns collapse to a single `{ name: null, rhp: ... }` entry
    // (TargetMarketIndicatorsDto.BuildOptionsByTimeHorizon's own Sop branch) — a null name simply
    // finds no match below, a harmless no-op, consistent with IC having no investment options to jump
    // to in the first place.
    selectInvestmentOptionByName(name) {
      if (name == null || this.investmentOptionsTab == null) {
        return
      }
      const list = this.investmentOptionsTab.sections.find((s) => s.sectionKey === 'InvestmentOptionsList')
      const payload = list && list.payload
      const options = payload ? [...(payload.sids || []), ...(payload.kiids || [])] : []
      const match = options.find((option) => option.name === name)
      if (match) {
        this.selectInvestmentOption(match.id, match.name)
      }
    },

    // Reuses this.productName/this.issuer/this.referenceDate as-is (the route's own original
    // params, not this.selectedRevision). investmentOptionName (the drill-down panel,
    // InvestmentOptionDetailPanel.vue's own per-option "Compara col mercato" links) is
    // base64url-encoded, same as every other investmentOptionName route param in this app.
    //
    // issuerName/structureType travel as query params so MarketComparison.vue/IndicatorsHistory.vue
    // don't need their own product-detail call to obtain them. Both values are
    // already sitting in this page's own already-loaded response: issuerName is a top-level
    // DetailPageResponse field, structureType is a Characteristics field (characteristicField, same
    // helper `currency` above uses).
    pushToRoute(routeName, investmentOptionName) {
      const params = {
        productName: this.productName,
        issuer: this.issuer,
        // referenceDate is optional on productDetails (the latest revision is loaded) but required on
        // marketComparison/indicatorsHistory: a missing param matches no route and ends on /error/404.
        referenceDate: this.referenceDate || this.formatReferenceDate(this.response.referenceDate),
      }
      if (investmentOptionName) {
        params.investmentOptionName = base64UrlEncode(investmentOptionName)
      }
      const query = {
        issuerName: this.response.issuerName,
        structureType: this.characteristicField('structureType'),
      }
      this.$router.push({ name: routeName, params, query })
    },

    formatReferenceDate(value) {
      if (typeof value !== 'string') {
        return value
      }
      return value.includes('T') ? value.split('T')[0] : value
    },

    loadDocumentation() {
      const data = {
        issuerCode: this.response.issuerCode,
        name: this.response.name,
        referenceDate: this.response.referenceDate,
      }
      this.loadProductDocumentation(data)
        .then((resp) => {
          if (resp != null) {
            this.documentation = resp
          }
        })
        .catch((e) => this.httpErrorsHandler(e))
    },

    async loadData() {
      this.loading = true

      const prodName = isBase64Url(this.productName) ? base64UrlDecode(this.productName) : this.productName

      if (this.selectedRevision != null && !isValidDate(this.selectedRevision)) {
        this.httpErrorsHandler('404')
        this.loading = false
        return
      }

      const data = { issuerCode: this.issuer, name: prodName, referenceDate: this.selectedRevision }

      try {
        const resp = await this.post('/api/detail-page/product', data)
        if (resp != null) {
          this.response = resp
          if (this.selectedRevision == null) {
            this.selectedRevision = this.formatReferenceDate(resp.referenceDate)
          }
          if (this.showDocumentationTab) {
            this.loadDocumentation()
          }
          // Deep-link support (mirrors views/ProductDetails.vue's own loadData(), its investmentOptionName
          // branch): open the "Opzioni di investimento" tab pre-activated when the URL already names one
          // — InvestmentOptionsListSection.vue's own mounted() (fires once this tab's `lazy` content
          // first renders) does the actual name→option lookup and selection.
          if (this.investmentOptionName && this.investmentOptionsTab) {
            this.activeTabIndex = 1
          }
          // Same permission + ownership check as canViewProductBenchmarking above, done inline
          // here since it reads resp directly rather than going through this.response's reactive
          // getter chain.
          if (this.canViewScoringProfiles && resp.benchmarkingAccess.isOwnedByUser) {
            this.loadScoringProfiles(resp.productId).catch(() => {
              this.$bvModal.msgBoxOk(this.$t('common.genericError'))
            })
          }
        }
      } catch (e) {
        this.httpErrorsHandler(e)
      }

      this.loading = false
    },
  },
  mounted() {
    if (this.productName === undefined) {
      return this.$router.push('/search')
    }
    this.selectedRevision = this.formatReferenceDate(this.referenceDate)
    this.loadData()
  },
}
</script>

<!-- Scoped, unlike the global block below: `.fairmat-secondary-tabs` is a plain CSS class, so a
global rule would leak to any other <b-tabs> using it. `.fairmat-secondary-tabs` is authored in THIS
template, so it carries this component's scope attribute and `/deep/` reaches b-tabs' internal
`.tab-pane` from there without escaping into a different component's tree. -->
<style lang="scss" scoped>
// Breathing room around the FKE "Descrizione prodotto" block so its multicolor border/glow
// is detached from the tab container edges.
.product-description-tab {
  padding: 20px;
}

// Detaches the first element of a secondary tab (KPI KID / Target market / Payout) from the chip row
// above it — the Payout pane starts directly with a DetailCard, which has only `mb-4` and no top
// margin of its own, so its box was touching the chips. Applied to the pane's first child rather than
// to DetailCard itself: DetailCard is also the sidebar's and CostsAtExpiration's shell, where a top
// margin would change card-to-card spacing everywhere instead of just this one gap.
//
// 1rem, not an arbitrary value: it matches the `mt-3` the two panes that already looked right carry on
// their own first child (the KPI KID cross-link block in this template, TargetMarketSection.vue's
// root) — and those two are left untouched by this rule, since bootstrap's spacing utilities are
// `!important` and therefore win over it. So this only ever fills in a genuinely missing gap.
.fairmat-secondary-tabs /deep/ .tab-pane > *:first-child {
  margin-top: 1rem;
}

// Own copy, same values as IndicatorsHistory.vue's/MarketComparison.vue's own scoped copies —
// every detail-style page builds its own rather than sharing one.
.detailsTemplateProductName {
  font-size: 1.3rem;
}
.detailsTemplateIssuer {
  font-size: 1rem;
}
</style>

<!-- Deliberately global (not scoped). Styles `.results`/`td[role='cell']` tables inside the
KpiKid-tab components (ProductCosts.vue, ProductScenarios.vue, ProductPerformance.vue — all share
the `class="results"` convention) — those components' own scoped `/deep/` rules (same reasoning as
every card/spacing fix in the sidebar) can never reach this page's tree, a completely different
component. Kept here once, globally, instead of duplicated per typed section. -->
<style>
.results th {
  padding: 5px 10px;
  background-color: var(--primary);
  color: white;
}

.results th,
.results td {
  width: 20% !important;
}

.results th:nth-child(1),
.results td:nth-child(1) {
  width: 40% !important;
}

td[role='cell'] {
  padding: 5px 10px;
}
</style>
